import { isReversed } from './invoiceReversal';
import type { AgreementInvoice, SoldAddOn, SoldItem, SoldLines, SoldService } from '../types/agreements.types';

/** Every agreement item id found on a line of an invoice that has not been reversed. */
export function claimedItemIdsFromInvoices(invoices: AgreementInvoice[]): Set<string> {
    const ids = new Set<string>();
    for (const invoice of invoices) {
        if (isReversed(invoice)) continue;
        for (const line of invoice.lines) {
            for (const id of line.agreementItemIds) ids.add(id);
        }
    }
    return ids;
}

const markClaimed = <T extends SoldItem | SoldAddOn>(unit: T, claimed: Set<string>): T =>
    claimed.has(unit.id) && unit.status !== 'claimed' ? { ...unit, status: 'claimed' } : unit;

const applyToService = (service: SoldService, claimed: Set<string>): SoldService => {
    const items = service.items.map((item) => markClaimed(item, claimed));
    return items.every((item, i) => item === service.items[i]) ? service : { ...service, items };
};

/** Any item or add-on whose id is in `claimed` renders as claimed; everything
 *  else is untouched. Returns the same reference for an empty set so memoisation holds. */
export function applyClaimedItemIds(lines: SoldLines, claimed: Set<string>): SoldLines {
    if (claimed.size === 0) return lines;

    const services = lines.services.map((service) => applyToService(service, claimed));
    const addOns = lines.addOns.map((addOn) => markClaimed(addOn, claimed));

    const servicesChanged = services.some((service, i) => service !== lines.services[i]);
    const addOnsChanged = addOns.some((addOn, i) => addOn !== lines.addOns[i]);

    if (!servicesChanged && !addOnsChanged) return lines;

    return {
        services: servicesChanged ? services : lines.services,
        addOns: addOnsChanged ? addOns : lines.addOns,
    };
}
